// Next-bath reminder card. Displays how long since the last logged grooming
// session, the target cadence (editable), and a one-tap "Set reminder"
// button that schedules a bath reminder using the existing /api/reminders
// dispatcher.
import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO, addDays, differenceInCalendarDays } from "date-fns";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Droplets, BellRing } from "lucide-react";

export default function BathCadenceCard({ dog }) {
  const [cadence, setCadence] = useState(21);
  const [pending, setPending] = useState(21);        // input value pre-save
  const [lastSessionDate, setLastSessionDate] = useState(null);
  const [scheduling, setScheduling] = useState(false);

  const load = useCallback(async () => {
    if (!dog?.id) return;
    try {
      const [settingsRes, sessionsRes] = await Promise.all([
        api.get("/grooming/settings", { params: { dog_id: dog.id } }),
        api.get("/grooming/sessions", { params: { dog_id: dog.id } }),
      ]);
      const days = settingsRes.data?.cadence_days || 21;
      setCadence(days);
      setPending(days);
      const items = sessionsRes.data?.items || [];
      setLastSessionDate(items[0]?.session_date || null);
    } catch (err) {
      // Silent failure — the card just renders defaults.
      void err;
    }
  }, [dog?.id]);

  useEffect(() => { load(); }, [load]);

  const nextBathDate = useMemo(() => {
    if (!lastSessionDate) return null;
    return addDays(parseISO(lastSessionDate), cadence);
  }, [lastSessionDate, cadence]);

  const daysUntil = useMemo(() => {
    if (!nextBathDate) return null;
    return differenceInCalendarDays(nextBathDate, new Date());
  }, [nextBathDate]);

  const saveCadence = async () => {
    const clamped = Math.max(1, Math.min(365, Number(pending) || 21));
    try {
      await api.put("/grooming/settings", { dog_id: dog.id, cadence_days: clamped });
      setCadence(clamped);
      setPending(clamped);
      toast.success(`Cadence set to every ${clamped} days`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
    }
  };

  const scheduleReminder = async () => {
    if (!nextBathDate) {
      toast.info("Log a grooming session first so we know when to nudge you.");
      return;
    }
    setScheduling(true);
    try {
      // If next bath date is already in the past, push it to tomorrow.
      const target = nextBathDate < new Date() ? addDays(new Date(), 1) : nextBathDate;
      await api.post("/reminders", {
        dog_id: dog.id,
        kind: "custom",
        title: "Bath day",
        notes: `Cadence: every ${cadence} days from the last groom.`,
        frequency: "once",
        days_of_week: [],
        time_of_day: "09:00",
        tz_offset_minutes: new Date().getTimezoneOffset(),
        run_once_date: format(target, "yyyy-MM-dd"),
        enabled: true,
      });
      toast.success(`Bath reminder set for ${format(target, "MMM d")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not schedule");
    } finally {
      setScheduling(false);
    }
  };

  const statusText = (() => {
    if (!lastSessionDate) return "Log a session to start the cadence clock.";
    if (daysUntil === null) return "";
    if (daysUntil < 0) return `Overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? "" : "s"} — bath time.`;
    if (daysUntil === 0) return "Next bath is today.";
    if (daysUntil === 1) return "Next bath is tomorrow.";
    return `Next bath in ${daysUntil} days.`;
  })();

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="bath-cadence-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Droplets size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Bath rhythm</p>
            <p className="font-display text-lg font-bold tracking-tight mt-0.5" data-testid="bath-status-text">
              {statusText}
            </p>
            {lastSessionDate ? (
              <p className="text-xs text-muted-foreground mt-1">
                Last groom {format(parseISO(lastSessionDate), "MMM d, yyyy")} · target every {cadence} days
                {nextBathDate ? ` · next ${format(nextBathDate, "MMM d")}` : ""}.
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Label htmlFor="bath-cadence" className="text-xs uppercase tracking-wider text-muted-foreground">
              Every
            </Label>
            <Input
              id="bath-cadence"
              type="number"
              min={1}
              max={365}
              value={pending}
              onChange={(e) => setPending(e.target.value)}
              className="w-20 h-9 text-sm"
              data-testid="bath-cadence-input"
            />
            <span className="text-sm text-muted-foreground">days</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={saveCadence}
              disabled={Number(pending) === cadence}
              data-testid="bath-cadence-save"
            >
              Save
            </Button>
          </div>
          <Button
            type="button"
            onClick={scheduleReminder}
            disabled={scheduling || !nextBathDate}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="bath-set-reminder"
          >
            <BellRing size={14} className="mr-1.5" /> {scheduling ? "Scheduling…" : "Set reminder"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
